import { Capacitor } from '@capacitor/core';
import { BasePrinterExecutor } from './BaseExecutor';

const ENABLE_BLE = false;
const BLE_DISABLED_MESSAGE =
  'Bluetooth Low Energy scanning/printing is temporarily disabled. Please use Bluetooth Classic.';

const BLE_SERVICE_CANDIDATES = [0xffe0, 0xff02, 0xffd0, 0xffb0];
const BLE_CHARACTERISTIC_CANDIDATES = [0xffe1, 0xff01, 0xffd1, 0xffb2];
const BLE_CHUNK_SIZE = 160;
const CLASSIC_CHUNK_SIZE = 512;
const CLASSIC_WRITE_DELAY_MS = 40;

const toUuid128 = (uuid) => {
  if (typeof uuid === 'string') {
    if (uuid.includes('-')) {
      return uuid.toLowerCase();
    }
    const sanitized = uuid.startsWith('0x') ? uuid.slice(2) : uuid;
    const parsed = parseInt(sanitized, 16);
    if (!Number.isNaN(parsed)) {
      const hexString = parsed.toString(16).padStart(4, '0');
      return `0000${hexString}-0000-1000-8000-00805f9b34fb`;
    }
  }
  const numeric = Number(uuid);
  const hex = Number.isNaN(numeric) ? '0000' : numeric.toString(16).padStart(4, '0');
  return `0000${hex}-0000-1000-8000-00805f9b34fb`;
};

const base64Encode = (bytes) => {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(bytes).toString('base64');
  }
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
};

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitFor = async (predicate, timeout = 2500, interval = 100) => {
  const start = Date.now();
  while (Date.now() - start < timeout) {
    const value = predicate();
    if (value) {
      return value;
    }
    await wait(interval);
  }
  return predicate();
};

const isCapacitorAndroid = () => {
  if (!Capacitor || !Capacitor.isNativePlatform?.()) return false;
  const platform = Capacitor.getPlatform?.() || Capacitor.platform;
  return platform === 'android';
};

const normalizeError = (error) => {
  if (!error) return 'Unknown Bluetooth error.';
  if (typeof error === 'string') return error;
  if (typeof error === 'object' && error.message) return error.message;
  if (typeof error === 'object' && error.error) return error.error;
  return String(error);
};

const dedupeDevices = (devices = []) => {
  const byId = new Map();
  devices.forEach((device) => {
    if (!device?.id) return;
    if (!byId.has(device.id)) {
      byId.set(device.id, device);
    } else {
      const existing = byId.get(device.id);
      byId.set(device.id, {
        ...existing,
        ...device,
        meta: {
          ...(existing.meta || {}),
          ...(device.meta || {}),
        },
      });
    }
  });
  return Array.from(byId.values());
};

const guessModeFromDescriptor = (descriptor = {}, context = {}) => {
  const metaMode = descriptor.meta?.mode || descriptor.mode || context.mode;
  if (metaMode) {
    return metaMode;
  }
  if (descriptor.address && descriptor.address.includes(':')) {
    return 'classic';
  }
  if (descriptor.id && descriptor.id.includes(':')) {
    return 'classic';
  }
  if (descriptor.meta?.rssi !== undefined) {
    return 'ble';
  }
  return null;
};

export class CapacitorBluetoothPrinterExecutor extends BasePrinterExecutor {
  constructor() {
    super({ type: 'capacitor-android-bluetooth', environment: 'capacitor' });
    this.bluetoothLE = null;
    this.classicPlugin = null;
    this.bleInitialized = false;
    this.connectedDevice = null;
    this.activeMode = null;
    this.serviceUuid = null;
    this.characteristicUuid = null;
    this.scanListener = null;
  }

  supportsClassic() {
    return isCapacitorAndroid();
  }

  supportsBle() {
    return ENABLE_BLE && isCapacitorAndroid();
  }

  async ensureBle() {
    if (!this.supportsBle()) {
      return null;
    }
    if (this.bluetoothLE) {
      return this.bluetoothLE;
    }
    const module = await import('@capacitor-community/bluetooth-le');
    this.bluetoothLE = module.BluetoothLe;
    if (!this.bleInitialized) {
      await this.bluetoothLE.initialize();
      this.bleInitialized = true;
    }
    return this.bluetoothLE;
  }

  async ensureNearbyPermissions() {
    if (!this.supportsBle()) {
      return true;
    }
    const bluetooth = await this.ensureBle();
    if (!bluetooth || !this.bleInitialized) {
      throw new Error('Bluetooth bridge is not available. Ensure @capacitor-community/bluetooth-le is installed and synced.');
    }
    if (typeof bluetooth.requestPermissions === 'function') {
      try {
        await bluetooth.requestPermissions();
        return true;
      } catch (error) {
        console.warn('[Printer] Bluetooth permission request failed', error);
        throw new Error(normalizeError(error) || 'Bluetooth permission request was denied.');
      }
    }
    return true;
  }

  async ensureBlePermissions() {
    if (!this.supportsBle()) {
      return true;
    }
    try {
      await this.ensureNearbyPermissions();
      return true;
    } catch (error) {
      console.warn('[Printer] ensureBlePermissions failed', error);
      return false;
    }
  }

  async ensureClassicPlugin() {
    if (!this.supportsClassic()) {
      return null;
    }
    if (this.classicPlugin) {
      return this.classicPlugin;
    }
    const plugin = await waitFor(() => (typeof window !== 'undefined' ? window.bluetoothSerial : null));
    if (plugin) {
      this.classicPlugin = plugin;
    }
    return this.classicPlugin;
  }

  async ensureClassicReady(options = {}) {
    const plugin = await this.ensureClassicPlugin();
    if (!plugin) {
      throw new Error(
        'Bluetooth Serial bridge is unavailable. Rebuild the Android shell with cordova-plugin-bluetooth-serial and run npx cap sync android.',
      );
    }

    try {
      await this.ensureNearbyPermissions();
    } catch (error) {
      if (options.ignorePermissionErrors) {
        console.warn('[Printer] Nearby permission request failed but continuing', error);
      } else {
        throw error;
      }
    }

    const enabled = await new Promise((resolve) => {
      if (typeof plugin.isEnabled !== 'function') {
        resolve(true);
        return;
      }
      try {
        plugin.isEnabled(
          () => resolve(true),
          () => resolve(false),
        );
      } catch (error) {
        console.warn('[Printer] bluetoothSerial.isEnabled failed', error);
        resolve(true);
      }
    });

    if (!enabled) {
      if (options.autoEnable === false || typeof plugin.enable !== 'function') {
        throw new Error('Bluetooth is turned off. Enable it in Android Settings and retry.');
      }
      await new Promise((resolve, reject) => {
        try {
          plugin.enable(
            () => resolve(),
            (error) => reject(new Error(normalizeError(error) || 'Failed to enable Bluetooth radio.')),
          );
        } catch (error) {
          reject(error);
        }
      });
    }

    return plugin;
  }

  getCapabilities() {
    const ble = this.supportsBle();
    const classic = this.supportsClassic();
    const supported = ble || classic;
    const notes = [];
    if (classic) {
      notes.push('Lists bonded + discoverable Bluetooth Classic printers. Pair in Android settings first if needed.');
    }
    if (ble) {
      notes.push('BLE fallback uses ESC/POS UUIDs (FFE0/FFE1). Adjust in Advanced Options if your printer differs.');
    }
    if (!supported) {
      notes.push('Capacitor Bluetooth bridge not available on this platform.');
    }
    return {
      canDiscover: supported,
      canPair: supported,
      canPrint: supported,
      transports: supported ? ['bluetooth'] : [],
      persistentConnection: supported,
      notes,
    };
  }

  mapBleDevice(device) {
    if (!device) return null;
    return {
      id: device.deviceId || device.id,
      name: device.name || device.localName || 'BLE Printer',
      transport: 'bluetooth',
      address: device.deviceId,
      meta: {
        mode: 'ble',
        rssi: device.rssi ?? null,
      },
    };
  }

  mapClassicDevice(device, { paired = true } = {}) {
    if (!device) return null;
    const id = device.address || device.id || device.uuid;
    if (!id) return null;
    return {
      id,
      name: device.name || device.address || 'Bluetooth Printer',
      transport: 'bluetooth',
      address: device.address || device.id,
      meta: {
        mode: 'classic',
        paired,
        classId: device.class ?? device.deviceClass ?? null,
      },
    };
  }

  async discoverClassicDevices(options = {}) {
    const plugin = await this.ensureClassicReady({ autoEnable: options.autoEnable !== false });
    const devices = [];
    try {
      const paired = await new Promise((resolve, reject) => {
        try {
          plugin.list(
            (result) => resolve(Array.isArray(result) ? result : []),
            (error) => reject(new Error(normalizeError(error))),
          );
        } catch (error) {
          reject(error);
        }
      });
      paired.forEach((item) => {
        const mapped = this.mapClassicDevice(item, { paired: true });
        if (mapped) {
          devices.push(mapped);
        }
      });
    } catch (error) {
      console.warn('[Printer] Failed to list paired Bluetooth devices', error);
      throw error;
    }

    if (options.includeUnpaired !== false && typeof plugin.discoverUnpaired === 'function') {
      try {
        const unpaired = await new Promise((resolve, reject) => {
          try {
            plugin.discoverUnpaired(
              (result) => resolve(Array.isArray(result) ? result : []),
              (error) => reject(new Error(normalizeError(error))),
            );
          } catch (error) {
            reject(error);
          }
        });
        unpaired.forEach((item) => {
          const mapped = this.mapClassicDevice(item, { paired: false });
          if (mapped) {
            devices.push(mapped);
          }
        });
      } catch (error) {
      console.warn('[Printer] Failed to discover unpaired Bluetooth devices', error);
      if (devices.length === 0) {
        throw error;
      }
      }
    }

    return dedupeDevices(devices);
  }

  async discoverBleDevices(options = {}) {
    const bluetooth = await this.ensureBle();
    if (!bluetooth) {
      return [];
    }
    await this.ensureBlePermissions();

    if (typeof bluetooth.requestDevice === 'function') {
      try {
        const response = await bluetooth.requestDevice({
          optionalServices: (options.optionalServices || BLE_SERVICE_CANDIDATES).map(toUuid128),
        });
        if (response?.deviceId) {
          const mapped = this.mapBleDevice(response);
          return mapped ? [mapped] : [];
        }
        return [];
      } catch (error) {
        throw new Error(normalizeError(error));
      }
    }

    const results = new Map();
    const duration = options.scanDuration || 5000;
    this.scanListener = await bluetooth.addListener('onResult', (event) => {
      if (event?.device?.deviceId) {
        const mapped = this.mapBleDevice(event.device);
        if (mapped) {
          results.set(event.device.deviceId, mapped);
        }
      }
    });

    const scanOptions = await bluetooth.requestLEScan({
      allowDuplicates: false,
      services: (options.services || BLE_SERVICE_CANDIDATES).map(toUuid128),
    });

    await wait(duration);

    await bluetooth.stopLEScan({
      scanId: scanOptions?.scanId,
    });
    if (this.scanListener) {
      await this.scanListener.remove();
      this.scanListener = null;
    }

    return Array.from(results.values());
  }

  async discoverDevices(options = {}) {
    const includeClassic = options.transport !== 'ble';
    const includeBle = options.transport !== 'classic';

    const devices = [];
    let bleError = null;
    let classicError = null;

    if (includeClassic && this.supportsClassic()) {
      try {
        devices.push(...(await this.discoverClassicDevices(options)));
      } catch (error) {
        classicError = error;
      }
    }

    if (includeBle && this.supportsBle()) {
      try {
        devices.push(...(await this.discoverBleDevices(options)));
      } catch (error) {
        bleError = error;
      }
    } else if (includeBle && !this.supportsBle()) {
      bleError = new Error(BLE_DISABLED_MESSAGE);
    }

    const merged = dedupeDevices(devices);
    if (merged.length > 0) {
      return {
        success: true,
        devices: merged,
      };
    }

    return {
      success: false,
      devices: [],
      message: bleError?.message || classicError?.message || 'No printers discovered.',
    };
  }

  async connect(deviceDescriptor = {}, options = {}) {
    const preferredRaw =
      options.transport ||
      guessModeFromDescriptor(deviceDescriptor) ||
      (this.supportsClassic() ? 'classic' : 'ble');
    const preferred = (preferredRaw || '').toLowerCase() || (this.supportsClassic() ? 'classic' : 'ble');

    if (preferred === 'classic') {
      const classicResult = await this.connectClassic(deviceDescriptor, options);
      if (classicResult.success || !this.supportsBle()) {
        return classicResult;
      }
      return this.connectBle(deviceDescriptor, options);
    }

    const bleResult = await this.connectBle(deviceDescriptor, options);
    if (bleResult.success || !this.supportsClassic()) {
      return bleResult;
    }
    return this.connectClassic(deviceDescriptor, options);
  }

  async connectClassic(deviceDescriptor = {}, options = {}) {
    let plugin;
    try {
      plugin = await this.ensureClassicReady({ autoEnable: options.autoEnable !== false });
    } catch (error) {
      return { success: false, message: error.message };
    }
    const address = deviceDescriptor.address || deviceDescriptor.id;
    if (!address) {
      return { success: false, message: 'Device address is required for Bluetooth Classic printers.' };
    }

    try {
      await new Promise((resolve, reject) => {
        let settled = false;
        const onSuccess = () => {
          if (!settled) {
            settled = true;
            resolve();
          }
        };
        const onError = (error) => {
          const normalized = new Error(normalizeError(error));
          if (!settled) {
            settled = true;
            reject(normalized);
          } else {
            console.warn('[Printer] Classic connection lost', normalized);
          }
        };
        const connectFn = options.insecure ? plugin.connectInsecure : plugin.connect;
        if (typeof connectFn !== 'function') {
          reject(new Error('Bluetooth Serial connect method unavailable.'));
          return;
        }
        connectFn.call(plugin, address, onSuccess, onError);
      });

      this.connectedDevice = {
        id: address,
        name: deviceDescriptor.name || deviceDescriptor.id || 'Bluetooth Printer',
        transport: 'bluetooth',
        address,
        meta: {
          ...(deviceDescriptor.meta || {}),
          mode: 'classic',
        },
      };
      this.activeMode = 'classic';
      this.serviceUuid = null;
      this.characteristicUuid = null;
      return { success: true, device: this.connectedDevice };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to connect to printer.' };
    }
  }

  async connectBle(deviceDescriptor = {}, options = {}) {
    if (!this.supportsBle()) {
      return { success: false, message: BLE_DISABLED_MESSAGE };
    }
    const bluetooth = await this.ensureBle();
    if (!bluetooth) {
      return { success: false, message: 'Bluetooth LE plugin not available.' };
    }
    await this.ensureBlePermissions();
    const deviceId = deviceDescriptor?.id || deviceDescriptor?.deviceId || deviceDescriptor?.address;
    if (!deviceId) {
      return { success: false, message: 'Device identifier is required for BLE printers.' };
    }

    try {
      await bluetooth.connect({ deviceId });
      this.connectedDevice = this.mapBleDevice({ deviceId, name: deviceDescriptor?.name });
      if (this.connectedDevice) {
        this.connectedDevice.meta = {
          ...(this.connectedDevice.meta || {}),
          mode: 'ble',
        };
      }
      this.serviceUuid = toUuid128(options.serviceUuid || BLE_SERVICE_CANDIDATES[0]);
      this.characteristicUuid = toUuid128(options.characteristicUuid || BLE_CHARACTERISTIC_CANDIDATES[0]);
      this.activeMode = 'ble';
      return { success: true, device: this.connectedDevice };
    } catch (error) {
      return { success: false, message: normalizeError(error) };
    }
  }

  async disconnect() {
    if (!this.connectedDevice) {
      this.activeMode = null;
      return { success: true };
    }

    if (this.activeMode === 'classic') {
      const plugin = await this.ensureClassicPlugin();
      this.connectedDevice = null;
      this.activeMode = null;
      if (!plugin) {
        return { success: true };
      }
      return new Promise((resolve) => {
        plugin.disconnect?.(
          () => resolve({ success: true }),
          (error) => resolve({ success: false, message: normalizeError(error) }),
        );
      });
    }

    const bluetooth = await this.ensureBle();
    if (!bluetooth || !this.connectedDevice?.id) {
      this.connectedDevice = null;
      this.activeMode = null;
      return { success: true };
    }
    try {
      await bluetooth.disconnect({ deviceId: this.connectedDevice.id });
      this.connectedDevice = null;
      this.activeMode = null;
      return { success: true };
    } catch (error) {
      return { success: false, message: normalizeError(error) };
    }
  }

  async print(data, options = {}) {
    if (this.activeMode === 'classic') {
      return this.printClassic(data);
    }
    return this.printBle(data, options);
  }

  async printClassic(data) {
    let plugin;
    try {
      plugin = await this.ensureClassicReady();
    } catch (error) {
      return { success: false, message: error.message };
    }
    if (!this.connectedDevice?.id) {
      return { success: false, message: 'No printer connected.' };
    }
    const payload =
      data instanceof Uint8Array
        ? data
        : Array.isArray(data)
          ? Uint8Array.from(data)
          : new Uint8Array(data || []);

    try {
      for (let offset = 0; offset < payload.length; offset += CLASSIC_CHUNK_SIZE) {
        const chunk = payload.slice(offset, offset + CLASSIC_CHUNK_SIZE);
        await new Promise((resolve, reject) => {
          plugin.write(
            chunk,
            () => resolve(),
            (error) => reject(new Error(normalizeError(error))),
          );
        });
        await wait(CLASSIC_WRITE_DELAY_MS);
      }
      return { success: true };
    } catch (error) {
      return { success: false, message: error.message || 'Failed to send data to printer.' };
    }
  }

  async printBle(data, options = {}) {
    if (!this.supportsBle()) {
      return { success: false, message: BLE_DISABLED_MESSAGE };
    }
    const bluetooth = await this.ensureBle();
    if (!bluetooth) {
      return { success: false, message: 'Bluetooth LE plugin not available.' };
    }
    if (!this.connectedDevice?.id) {
      return { success: false, message: 'No printer connected.' };
    }

    const serviceUuid = toUuid128(options.serviceUuid || this.serviceUuid || BLE_SERVICE_CANDIDATES[0]);
    const characteristicUuid = toUuid128(
      options.characteristicUuid || this.characteristicUuid || BLE_CHARACTERISTIC_CANDIDATES[0],
    );

    const payload =
      data instanceof Uint8Array
        ? data
        : Array.isArray(data)
          ? Uint8Array.from(data)
          : new Uint8Array(data || []);

    try {
      for (let offset = 0; offset < payload.length; offset += BLE_CHUNK_SIZE) {
        const chunk = payload.slice(offset, offset + BLE_CHUNK_SIZE);
        await bluetooth.write({
          deviceId: this.connectedDevice.id,
          service: serviceUuid,
          characteristic: characteristicUuid,
          value: base64Encode(chunk),
          withoutResponse: true,
        });
      }
      return { success: true };
    } catch (error) {
      return { success: false, message: normalizeError(error) };
    }
  }

  async getStatus() {
    if (!this.connectedDevice) {
      return { connected: false };
    }
    return {
      connected: true,
      device: this.connectedDevice,
      serviceUuid: this.serviceUuid,
      characteristicUuid: this.characteristicUuid,
      mode: this.activeMode,
    };
  }

  async restoreConnection(persistedState = {}) {
    if (!persistedState?.device?.id) {
      return { success: false, connected: false };
    }
    const mode = guessModeFromDescriptor(persistedState.device, persistedState.context);
    const result = await this.connect(persistedState.device, {
      transport: mode,
      serviceUuid: persistedState.serviceUuid,
      characteristicUuid: persistedState.characteristicUuid,
    });
    return { ...result, connected: result.success };
  }
}

